import type { OperationStatus } from "../lib/operationStatus.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ColumnFilterPopover, IconFunnel } from "../components/ColumnFilterPopover.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch } from "../lib/api.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";

type OperationKind = "unclassified" | "monitor" | "materialize" | "visualize";

type LineageRef = { id: string; name: string };

type OperationLineage = {
  pages: LineageRef[];
  pdos: LineageRef[];
  watches: LineageRef[];
  schedules: LineageRef[];
};

// Which door created the operation — drives the User-Created / Agent-Created
// tabs. `system` is the hub's unstamped default and groups with user-created.
type OperationCreatedVia = "console" | "mcp" | "system";

// One study chip: the human study reference plus the detail-route slug.
type StudyLinkRef = { id: string; slug: string; title: string };

type OperationListItem = {
  id: string;
  kind: OperationKind;
  name: string;
  description: string | null;
  // ADR-0154 §6.3 C7 — one console home; this copy was missing "validate".
  status: OperationStatus;
  owner_account_id: string;
  owner_email: string | null;
  // ADR-0096: tags is reserved on the server side — not surfaced in UI.
  tags: string[];
  updated_at: string;
  created_via: OperationCreatedVia;
  study_links: StudyLinkRef[];
  lineage: OperationLineage;
};

type OperationsTab = "user" | "agent";

type SortKey = "name" | "status" | "updated" | "page" | "pdo" | "watch" | "schedule";
type SortDir = "asc" | "desc";

type FilterCategory = "page_id" | "pdo_id" | "watch_id" | "schedule_id";

type FilterOption = { id: string; label: string };

const BLANK_KEY_BY_CATEGORY: Record<FilterCategory, string> = {
  page_id: "page_blank",
  pdo_id: "pdo_blank",
  watch_id: "watch_blank",
  schedule_id: "schedule_blank"
};

function defaultDirFor(sort: SortKey): SortDir {
  return sort === "updated" ? "desc" : "asc";
}

// Build the API query string from URL params. Encodes the four repeatable
// lineage filter ids. Sort/dir are NOT sent on the wire — sort is computed
// client-side to avoid a network round-trip + flash on every column click.
function buildOperationsQs(params: URLSearchParams): string {
  const qs = new URLSearchParams();
  for (const cat of ["page_id", "pdo_id", "watch_id", "schedule_id"] as const) {
    for (const v of params.getAll(cat)) qs.append(cat, v);
  }
  for (const blankKey of ["page_blank", "pdo_blank", "watch_blank", "schedule_blank"] as const) {
    if (params.get(blankKey) === "true") qs.set(blankKey, "true");
  }
  return qs.toString();
}

// Key over only the params that affect server fetch — used to dedupe useEffect
// triggers when the user only changes sort/dir.
function filterFetchKey(params: URLSearchParams): string {
  const idCats = ["page_id", "pdo_id", "watch_id", "schedule_id"] as const;
  const blankCats = ["page_blank", "pdo_blank", "watch_blank", "schedule_blank"] as const;
  const idPart = idCats.map((c) => [c, ...params.getAll(c).slice().sort()].join("=")).join("&");
  const blankPart = blankCats.map((c) => `${c}=${params.get(c) === "true" ? "1" : "0"}`).join("&");
  return `${idPart}|${blankPart}`;
}

export function OperationsList() {
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  // `loading` is only true for the very first fetch; subsequent refetches use
  // `refreshing` so the table stays on-screen and the user doesn't see a flash.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<OperationListItem[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Filter popover state — which column header is open + its anchor element.
  const [openFilter, setOpenFilter] = useState<{ category: FilterCategory; anchor: HTMLElement } | null>(null);

  // Filter options for each popover. Fetched lazily on first open.
  const [pageOptions, setPageOptions] = useState<FilterOption[] | null>(null);
  const [pdoOptions, setPdoOptions] = useState<FilterOption[] | null>(null);
  const [watchOptions, setWatchOptions] = useState<FilterOption[] | null>(null);
  const [scheduleOptions, setScheduleOptions] = useState<FilterOption[] | null>(null);

  // Derived state from URL params.
  const tab: OperationsTab = searchParams.get("tab") === "agent" ? "agent" : "user";
  const sort = (searchParams.get("sort") as SortKey | null) ?? "updated";
  const dir = (searchParams.get("dir") as SortDir | null) ?? defaultDirFor(sort);
  const selectedPageIds = useMemo(() => searchParams.getAll("page_id"), [searchParams]);
  const selectedPdoIds = useMemo(() => searchParams.getAll("pdo_id"), [searchParams]);
  const selectedWatchIds = useMemo(() => searchParams.getAll("watch_id"), [searchParams]);
  const selectedScheduleIds = useMemo(() => searchParams.getAll("schedule_id"), [searchParams]);
  const pageBlank = searchParams.get("page_blank") === "true";
  const pdoBlank = searchParams.get("pdo_blank") === "true";
  const watchBlank = searchParams.get("watch_blank") === "true";
  const scheduleBlank = searchParams.get("schedule_blank") === "true";
  const hasAnyFilter =
    selectedPageIds.length > 0 ||
    selectedPdoIds.length > 0 ||
    selectedWatchIds.length > 0 ||
    selectedScheduleIds.length > 0 ||
    pageBlank ||
    pdoBlank ||
    watchBlank ||
    scheduleBlank;

  const fetchKey = filterFetchKey(searchParams);

  async function load(qs: string, isInitial: boolean) {
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const path = qs ? `/api/tenant/operations?${qs}` : "/api/tenant/operations";
      const data = await apiFetch<{ operations: OperationListItem[] }>(path);
      setRows(data.operations);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (isInitial) setLoading(false);
      else setRefreshing(false);
    }
  }

  // Refetch only when filter set changes. Sort/dir changes are client-side.
  const initialLoadRef = useRef(true);
  useEffect(() => {
    const qs = buildOperationsQs(searchParams);
    const isInitial = initialLoadRef.current;
    initialLoadRef.current = false;
    void load(qs, isInitial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  async function createOperation() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await apiFetch("/api/tenant/operations", {
        method: "POST",
        body: { name: name.trim(), description: description.trim() || null }
      });
      setCreateOpen(false);
      setName("");
      setDescription("");
      const qs = buildOperationsQs(searchParams);
      await load(qs, false);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  async function duplicateOperation(row: OperationListItem) {
    try {
      const duplicated = await apiFetch<{ operation: OperationListItem }>(
        `/api/tenant/operations/${encodeURIComponent(row.id)}/duplicate`,
        { method: "POST", body: {} }
      );
      const qs = buildOperationsQs(searchParams);
      await load(qs, false);
      nav(`/app/build/operations/${duplicated.operation.id}`);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function deleteOperation(row: OperationListItem) {
    const ok = await confirm.confirm({
      title: "Delete operation?",
      detail: `This permanently deletes “${row.name}” and every persistent data store created from its Persist nodes (all versions and materialized data). This cannot be undone.`,
      confirmLabel: "Delete"
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/operations/${encodeURIComponent(row.id)}`, { method: "DELETE" });
      const qs = buildOperationsQs(searchParams);
      await load(qs, false);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  // ── Sort + filter helpers ────────────────────────────────────────────────

  function toggleSort(key: SortKey) {
    const next = new URLSearchParams(searchParams);
    if (sort !== key) {
      next.set("sort", key);
      next.set("dir", defaultDirFor(key));
    } else if (dir === defaultDirFor(key)) {
      next.set("sort", key);
      next.set("dir", defaultDirFor(key) === "asc" ? "desc" : "asc");
    } else {
      // Third click: clear the sort entirely.
      next.delete("sort");
      next.delete("dir");
    }
    setSearchParams(next);
  }

  function addFilter(category: FilterCategory, id: string) {
    const next = new URLSearchParams(searchParams);
    if (!next.getAll(category).includes(id)) {
      next.append(category, id);
    }
    setSearchParams(next);
  }

  function removeFilter(category: FilterCategory, id: string) {
    const next = new URLSearchParams(searchParams);
    const remaining = next.getAll(category).filter((v) => v !== id);
    next.delete(category);
    for (const v of remaining) next.append(category, v);
    setSearchParams(next);
  }

  function toggleFilter(category: FilterCategory, id: string) {
    const current = searchParams.getAll(category);
    if (current.includes(id)) removeFilter(category, id);
    else addFilter(category, id);
  }

  function clearAllFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete("page_id");
    next.delete("pdo_id");
    next.delete("watch_id");
    next.delete("schedule_id");
    next.delete("page_blank");
    next.delete("pdo_blank");
    next.delete("watch_blank");
    next.delete("schedule_blank");
    setSearchParams(next);
  }

  function clearFilterCategory(category: FilterCategory) {
    const next = new URLSearchParams(searchParams);
    next.delete(category);
    next.delete(BLANK_KEY_BY_CATEGORY[category]);
    setSearchParams(next);
  }

  function toggleBlankFor(category: FilterCategory) {
    const next = new URLSearchParams(searchParams);
    const key = BLANK_KEY_BY_CATEGORY[category];
    if (next.get(key) === "true") {
      next.delete(key);
    } else {
      next.set(key, "true");
    }
    setSearchParams(next);
  }

  function isBlankSelectedFor(category: FilterCategory): boolean {
    if (category === "page_id") return pageBlank;
    if (category === "pdo_id") return pdoBlank;
    if (category === "watch_id") return watchBlank;
    if (category === "schedule_id") return scheduleBlank;
    return false;
  }

  // ── Filter option loaders ────────────────────────────────────────────────

  async function ensurePageOptions() {
    if (pageOptions !== null) return;
    try {
      const data = await apiFetch<{ pages: Array<{ id: string; title: string }> }>(
        "/api/tenant/pages"
      );
      setPageOptions(data.pages.map((p) => ({ id: p.id, label: p.title })));
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function ensurePdoOptions() {
    if (pdoOptions !== null) return;
    try {
      const data = await apiFetch<{ pdos: Array<{ id: string; name: string }> }>(
        "/api/tenant/pdos"
      );
      setPdoOptions(data.pdos.map((p) => ({ id: p.id, label: p.name })));
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function ensureWatchOptions() {
    if (watchOptions !== null) return;
    try {
      const data = await apiFetch<{
        watches: Array<{ id: string; name: string; operation_name: string }>;
      }>("/api/tenant/watch-nodes");
      setWatchOptions(
        data.watches.map((w) => ({
          id: w.id,
          label: w.name ? `${w.name} — ${w.operation_name}` : `Watch — ${w.operation_name}`
        }))
      );
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function ensureScheduleOptions() {
    if (scheduleOptions !== null) return;
    try {
      const data = await apiFetch<{ schedules: Array<{ id: string; name: string }> }>(
        "/api/tenant/schedules"
      );
      setScheduleOptions(data.schedules.map((s) => ({ id: s.id, label: s.name })));
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function openFilterFor(category: FilterCategory, anchor: HTMLElement) {
    if (category === "page_id") await ensurePageOptions();
    else if (category === "pdo_id") await ensurePdoOptions();
    else if (category === "watch_id") await ensureWatchOptions();
    else if (category === "schedule_id") await ensureScheduleOptions();
    setOpenFilter({ category, anchor });
  }

  function currentOptionsFor(category: FilterCategory): FilterOption[] | null {
    if (category === "page_id") return pageOptions;
    if (category === "pdo_id") return pdoOptions;
    if (category === "watch_id") return watchOptions;
    if (category === "schedule_id") return scheduleOptions;
    return null;
  }

  function currentSelectionFor(category: FilterCategory): string[] {
    if (category === "page_id") return selectedPageIds;
    if (category === "pdo_id") return selectedPdoIds;
    if (category === "watch_id") return selectedWatchIds;
    if (category === "schedule_id") return selectedScheduleIds;
    return [];
  }

  function isCategoryFilterActive(category: FilterCategory): boolean {
    return currentSelectionFor(category).length > 0 || isBlankSelectedFor(category);
  }

  function setTab(next: OperationsTab) {
    const params = new URLSearchParams(searchParams);
    if (next === "agent") params.set("tab", "agent");
    else params.delete("tab");
    setSearchParams(params);
  }

  // ── Client-side sort ─────────────────────────────────────────────────────

  const sortedRows = useMemo(() => sortClient(rows, sort, dir), [rows, sort, dir]);
  // Tab split is client-side, like sort — one fetch serves both tabs.
  const userRows = useMemo(() => sortedRows.filter((r) => r.created_via !== "mcp"), [sortedRows]);
  const agentRows = useMemo(() => sortedRows.filter((r) => r.created_via === "mcp"), [sortedRows]);
  const tabRows = tab === "agent" ? agentRows : userRows;

  function sortIndicator(key: SortKey): string {
    if (sort !== key) return "";
    return dir === "asc" ? " ▲" : " ▼";
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <PageLayout
      title="Operations"
      actions={
        <button className="adminNewLink" type="button" onClick={() => setCreateOpen(true)}>
          + New operation
        </button>
      }
    >
      <div className="tabs">
        <button type="button" className={`tab${tab === "user" ? " tabActive" : ""}`} onClick={() => setTab("user")}>
          User-Created{!loading && !error ? ` (${userRows.length})` : ""}
        </button>
        <button type="button" className={`tab${tab === "agent" ? " tabActive" : ""}`} onClick={() => setTab("agent")}>
          Agent-Created{!loading && !error ? ` (${agentRows.length})` : ""}
        </button>
      </div>
      <div className="card adminList">
        {hasAnyFilter ? (
          <div className="toolbar">
            <div className="toolbarGroup">
              <button className="btn" type="button" onClick={clearAllFilters}>
                Clear all filters
              </button>
            </div>
          </div>
        ) : null}
        {loading ? <LoadingState /> : null}
        {!loading && error ? <ErrorState detail={error} /> : null}
        {!loading && !error && tabRows.length === 0 ? (
          hasAnyFilter ? (
            <div className="adminListEmpty">
              No operations match these filters.{" "}
              <button className="btn" type="button" onClick={clearAllFilters}>
                Clear filters
              </button>
            </div>
          ) : tab === "agent" ? (
            <div className="adminListEmpty">
              No agent-created operations yet. Operations built from Atlas-connected sessions land here.
            </div>
          ) : (
            <div className="adminListEmpty">No operations</div>
          )
        ) : null}
        {!loading && !error && tabRows.length > 0 ? (
          <div className="tableWrap" style={{ opacity: refreshing ? 0.6 : 1, transition: "opacity 120ms" }}>
            <table className="table">
              <thead>
                <tr>
                  <SortableTh label="Name" sortKey="name" current={sort} indicator={sortIndicator("name")} onToggle={toggleSort} />
                  <SortableTh label="Status" sortKey="status" current={sort} indicator={sortIndicator("status")} onToggle={toggleSort} />
                  {tab === "agent" ? <th>Studies</th> : null}
                  <FilterableSortableTh
                    label="Page"
                    sortKey="page"
                    current={sort}
                    indicator={sortIndicator("page")}
                    onToggleSort={toggleSort}
                    category="page_id"
                    filterActive={isCategoryFilterActive("page_id")}
                    onOpenFilter={(anchor) => void openFilterFor("page_id", anchor)}
                  />
                  <FilterableSortableTh
                    label="PDO"
                    sortKey="pdo"
                    current={sort}
                    indicator={sortIndicator("pdo")}
                    onToggleSort={toggleSort}
                    category="pdo_id"
                    filterActive={isCategoryFilterActive("pdo_id")}
                    onOpenFilter={(anchor) => void openFilterFor("pdo_id", anchor)}
                  />
                  <FilterableSortableTh
                    label="Watch"
                    sortKey="watch"
                    current={sort}
                    indicator={sortIndicator("watch")}
                    onToggleSort={toggleSort}
                    category="watch_id"
                    filterActive={isCategoryFilterActive("watch_id")}
                    onOpenFilter={(anchor) => void openFilterFor("watch_id", anchor)}
                  />
                  <FilterableSortableTh
                    label="Schedule"
                    sortKey="schedule"
                    current={sort}
                    indicator={sortIndicator("schedule")}
                    onToggleSort={toggleSort}
                    category="schedule_id"
                    filterActive={isCategoryFilterActive("schedule_id")}
                    onOpenFilter={(anchor) => void openFilterFor("schedule_id", anchor)}
                  />
                  <SortableTh label="Updated" sortKey="updated" current={sort} indicator={sortIndicator("updated")} onToggle={toggleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tabRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button
                        className="adminListName"
                        type="button"
                        onClick={() => nav(`/app/build/operations/${row.id}`)}
                      >
                        {row.name}
                      </button>
                    </td>
                    <td>{row.status}</td>
                    {tab === "agent" ? (
                      <StudiesCell
                        links={row.study_links}
                        onChipClick={(slug) => nav(`/app/studies/${encodeURIComponent(slug)}`)}
                      />
                    ) : null}
                    <LineageCell
                      refs={row.lineage.pages}
                      onChipClick={(id) => nav(`/app/build/pages/${id}`)}
                    />
                    <LineageCell refs={row.lineage.pdos} />
                    <LineageCell refs={row.lineage.watches} />
                    <LineageCell
                      refs={row.lineage.schedules}
                      onChipClick={(id) => nav(`/app/schedule/workflows/${id}`)}
                    />
                    <td>{new Date(row.updated_at).toLocaleString()}</td>
                    <td>
                      <div className="adminListActions">
                        <button
                          className="adminListAction"
                          type="button"
                          onClick={() => nav(`/app/build/operations/${row.id}`)}
                        >
                          Edit
                        </button>
                        <button
                          className="adminListAction"
                          type="button"
                          onClick={() => void duplicateOperation(row)}
                        >
                          Duplicate
                        </button>
                        <button
                          className="adminListAction isDanger"
                          type="button"
                          onClick={() => void deleteOperation(row)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {createOpen ? (
        <div className="modalOverlay" role="presentation" onClick={() => setCreateOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="reportModalTitle">New operation</div>
            <div className="field">
              <label className="label" htmlFor="op-name">
                Name
              </label>
              <input id="op-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <div className="field">
              <label className="label" htmlFor="op-description">
                Description
              </label>
              <textarea
                id="op-description"
                className="input reportTextarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="actions">
              <button className="btn" type="button" onClick={() => setCreateOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn btnPrimary"
                type="button"
                onClick={() => void createOperation()}
                disabled={saving || !name.trim()}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {openFilter ? (
        <ColumnFilterPopover
          anchor={openFilter.anchor}
          onClose={() => setOpenFilter(null)}
          minWidth={240}
          onClear={
            isCategoryFilterActive(openFilter.category)
              ? () => clearFilterCategory(openFilter.category)
              : undefined
          }
        >
          <FilterPopoverContent
            options={currentOptionsFor(openFilter.category)}
            selection={currentSelectionFor(openFilter.category)}
            blankSelected={isBlankSelectedFor(openFilter.category)}
            onToggle={(id) => toggleFilter(openFilter.category, id)}
            onToggleBlank={() => toggleBlankFor(openFilter.category)}
          />
        </ColumnFilterPopover>
      ) : null}
    </PageLayout>
  );
}

// ── Lineage cell ────────────────────────────────────────────────────────────

function LineageCell(props: { refs: LineageRef[]; onChipClick?: (id: string) => void }) {
  if (props.refs.length === 0) {
    return (
      <td>
        <span className="muted">—</span>
      </td>
    );
  }
  return (
    <td>
      <div className="pillList">
        {props.refs.map((ref) =>
          props.onChipClick ? (
            <button
              key={ref.id}
              type="button"
              className="pill"
              onClick={() => props.onChipClick!(ref.id)}
              title={ref.name}
            >
              {ref.name}
            </button>
          ) : (
            <span key={ref.id} className="pill" title={ref.name}>
              {ref.name}
            </span>
          )
        )}
      </div>
    </td>
  );
}

// ── Studies cell (agent tab) ────────────────────────────────────────────────
// The chip text is the human study reference (006, 002_a); the title carries
// the study's name on hover. Links are advisory — an operation with no chips
// simply isn't referenced by any study the viewer can see.

function StudiesCell(props: { links: StudyLinkRef[]; onChipClick: (slug: string) => void }) {
  if (props.links.length === 0) {
    return (
      <td>
        <span className="muted">—</span>
      </td>
    );
  }
  return (
    <td>
      <div className="pillList">
        {props.links.map((link) => (
          <button
            key={link.slug}
            type="button"
            className="pill"
            onClick={() => props.onChipClick(link.slug)}
            title={link.title}
          >
            <span className="mono">{link.id}</span>
          </button>
        ))}
      </div>
    </td>
  );
}

// ── Header cells ────────────────────────────────────────────────────────────

function SortableTh(props: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  indicator: string;
  onToggle: (key: SortKey) => void;
}) {
  return (
    <th>
      <button className="adminListName" type="button" onClick={() => props.onToggle(props.sortKey)}>
        {props.label}
        {props.indicator}
      </button>
    </th>
  );
}

function FilterableSortableTh(props: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  indicator: string;
  onToggleSort: (key: SortKey) => void;
  category: FilterCategory;
  filterActive: boolean;
  onOpenFilter: (anchor: HTMLElement) => void;
}) {
  const ref = useRef<HTMLButtonElement | null>(null);
  return (
    <th>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <button className="adminListName" type="button" onClick={() => props.onToggleSort(props.sortKey)}>
          {props.label}
          {props.indicator}
        </button>
        <button
          ref={ref}
          type="button"
          className={`thFilter${props.filterActive ? " isActive" : ""}`}
          onClick={() => {
            if (ref.current) props.onOpenFilter(ref.current);
          }}
          aria-label={`Filter ${props.label}`}
          title={`Filter ${props.label}`}
        >
          <IconFunnel size={14} filled={props.filterActive} />
        </button>
      </div>
    </th>
  );
}

// ── Filter popover content ──────────────────────────────────────────────────

function FilterPopoverContent(props: {
  options: FilterOption[] | null;
  selection: string[];
  blankSelected: boolean;
  onToggle: (id: string) => void;
  onToggleBlank: () => void;
}) {
  const [search, setSearch] = useState("");
  const optionsLoading = props.options === null;
  const q = search.trim().toLowerCase();
  const filtered =
    !optionsLoading && q
      ? props.options!.filter((o) => o.label.toLowerCase().includes(q))
      : props.options ?? [];
  return (
    <div style={{ display: "grid", gap: 6, minWidth: 240, maxHeight: 320, overflow: "auto" }}>
      <input
        className="input"
        placeholder="Search…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div style={{ display: "grid", gap: 2 }}>
        {/* (blank) is always visible at the top, even when the option list is
            empty or still loading — it represents the "no value" axis. */}
        <label className="colFilterCheckbox">
          <input type="checkbox" checked={props.blankSelected} onChange={props.onToggleBlank} />
          <span className="muted">(blank)</span>
        </label>
        {optionsLoading ? (
          <div className="muted" style={{ padding: 8 }}>Loading…</div>
        ) : (
          <>
            {filtered.map((opt) => {
              const checked = props.selection.includes(opt.id);
              return (
                <label key={opt.id} className="colFilterCheckbox">
                  <input type="checkbox" checked={checked} onChange={() => props.onToggle(opt.id)} />
                  <span>{opt.label}</span>
                </label>
              );
            })}
            {filtered.length === 0 ? (
              <div className="muted">{q ? "No matches." : "No options."}</div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

// ── Client-side sort ────────────────────────────────────────────────────────

function sortClient(rows: OperationListItem[], sort: SortKey, dir: SortDir): OperationListItem[] {
  const factor = dir === "desc" ? -1 : 1;
  const lineageKey = (refs: LineageRef[]): string | null => {
    if (refs.length === 0) return null;
    let min = refs[0]!.name;
    for (let i = 1; i < refs.length; i += 1) {
      const next = refs[i]!.name;
      if (next.localeCompare(min) < 0) min = next;
    }
    return min;
  };
  const lineageRefsFor = (row: OperationListItem, key: SortKey): LineageRef[] | null => {
    if (key === "page") return row.lineage.pages;
    if (key === "pdo") return row.lineage.pdos;
    if (key === "watch") return row.lineage.watches;
    if (key === "schedule") return row.lineage.schedules;
    return null;
  };
  const indexed = rows.map((row, idx) => ({ row, idx }));
  indexed.sort((a, b) => {
    const lineageA = lineageRefsFor(a.row, sort);
    if (lineageA !== null) {
      const lineageB = lineageRefsFor(b.row, sort)!;
      const ka = lineageKey(lineageA);
      const kb = lineageKey(lineageB);
      // Empties always to the bottom, independent of direction.
      if (ka === null && kb !== null) return 1;
      if (kb === null && ka !== null) return -1;
      if (ka !== null && kb !== null) {
        const p = ka.localeCompare(kb);
        if (p !== 0) return p * factor;
      }
    } else {
      let p = 0;
      if (sort === "name") p = a.row.name.localeCompare(b.row.name);
      else if (sort === "status") p = a.row.status.localeCompare(b.row.status);
      else if (sort === "updated") p = a.row.updated_at.localeCompare(b.row.updated_at);
      if (p !== 0) return p * factor;
    }
    // Secondary: updated_at DESC for determinism.
    const u = b.row.updated_at.localeCompare(a.row.updated_at);
    if (u !== 0) return u;
    return a.idx - b.idx;
  });
  return indexed.map((e) => e.row);
}
